import apiService from './apiService';
import { getApiBaseUrl } from '../config/apiConfig';
import { readPlanDeComFromFile } from '../utils/pdcExcelImport';

const PREFIX = '/kkp/plan-de-com';

async function request(promise) {
  const res = await promise;
  if (!res.success) {
    throw new Error(res.message || 'Erreur API');
  }
  return res;
}

async function fetchExportBlob(url, defaultFilename) {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}`, Accept: '*/*' } : { Accept: '*/*' },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Erreur export (HTTP ${response.status})`);
  }

  const rawBlob = await response.blob();
  const blob = rawBlob.type === 'application/pdf'
    ? rawBlob
    : new Blob([rawBlob], { type: 'application/pdf' });
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] || defaultFilename;

  return { blob, filename };
}

async function downloadExport(url, defaultFilename) {
  const { blob, filename } = await fetchExportBlob(url, defaultFilename);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function buildBdcPdfUrl(id, options = {}) {
  const base = getApiBaseUrl();
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '') {
      params.set(key, value);
    }
  });
  const query = params.toString();
  return `${base}${PREFIX}/dossiers/${id}/bdc/pdf${query ? `?${query}` : ''}`;
}

function bdcPdfFilename(options = {}) {
  return options.document_titre === 'DEVIS' ? 'DEVIS.pdf' : 'BDC_complet.pdf';
}

const pdcService = {
  async getMeta() {
    const res = await request(apiService.get(`${PREFIX}/meta`));
    return res.data;
  },

  async getDisponibilites(annee, mois) {
    const res = await request(apiService.get(`${PREFIX}/disponibilites`, { annee, mois }));
    return res.data;
  },

  async getSession() {
    const res = await request(apiService.get(`${PREFIX}/session`));
    return res.data;
  },

  async startNewSession() {
    const res = await request(apiService.post(`${PREFIX}/session/new`));
    return res.data;
  },

  async openDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/open`));
    return res.data;
  },

  async duplicateDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/duplicate`));
    return res.data;
  },

  async listDossiers({ search = '', page = 1, limit = 20, scope = 'mine', commercial_id = '' } = {}) {
    const res = await request(apiService.get(`${PREFIX}/dossiers`, {
      search,
      page,
      limit,
      scope,
      commercial_id: commercial_id || undefined,
    }));
    return { dossiers: res.data.dossiers ?? [], pagination: res.pagination ?? {} };
  },

  async getDossier(id) {
    const res = await request(apiService.get(`${PREFIX}/dossiers/${id}`));
    return res.data;
  },

  async createDossier(payload = {}) {
    const res = await request(apiService.post(`${PREFIX}/dossiers`, payload));
    return res.data;
  },

  async updateDossier(id, payload) {
    const res = await request(apiService.put(`${PREFIX}/dossiers/${id}`, payload));
    return res.data;
  },

  async deleteDossier(id) {
    const res = await request(apiService.delete(`${PREFIX}/dossiers/${id}`));
    return res.data;
  },

  async resetDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/reset`));
    return res.data;
  },

  async recalculateDossier(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/recalculate`));
    return res.data;
  },

  async getBdc(id) {
    const res = await request(apiService.get(`${PREFIX}/dossiers/${id}/bdc`));
    return res.data;
  },

  async prepareBdc(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/bdc/prepare`));
    return res.data;
  },

  async updateMediaPlanning(id, mediaPlanning) {
    const res = await request(apiService.put(`${PREFIX}/dossiers/${id}/media-planning`, { media_planning: mediaPlanning }));
    return res.data;
  },

  async importRows(id, { plan_matrix, media_matrix }) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/import-rows`, { plan_matrix, media_matrix }));
    return res.data;
  },

  async importFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request(apiService.post(`${PREFIX}/dossiers/import`, formData));
    return res.data;
  },

  async importFileToDossier(id, file) {
    const { plan_matrix, media_matrix } = await readPlanDeComFromFile(file);
    return this.importRows(id, { plan_matrix, media_matrix });
  },

  async generateDiapo(id) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/generate-diapo`, null, {}, { timeout: 180000 }));
    return res.data;
  },

  async pushDolibarr(id, bdcOptions = {}) {
    const res = await request(apiService.post(`${PREFIX}/dossiers/${id}/push-dolibarr`, {
      bdc_options: bdcOptions,
    }));
    return res.data;
  },

  exportDossier(id) {
    const base = getApiBaseUrl();
    return downloadExport(`${base}${PREFIX}/dossiers/${id}/export`, `plan-de-com-${id}.xlsx`);
  },

  fetchBdcPdf(id, options = {}) {
    return fetchExportBlob(buildBdcPdfUrl(id, options), bdcPdfFilename(options));
  },

  downloadBdcPdf(id, options = {}) {
    return downloadExport(buildBdcPdfUrl(id, options), bdcPdfFilename(options));
  },

  fetchMediaPlanningPdf(id) {
    const base = getApiBaseUrl();
    return fetchExportBlob(`${base}${PREFIX}/dossiers/${id}/media-planning/pdf`, 'media-planning.pdf');
  },

  downloadMediaPlanningPdf(id) {
    const base = getApiBaseUrl();
    return downloadExport(`${base}${PREFIX}/dossiers/${id}/media-planning/pdf`, 'media-planning.pdf');
  },

  async listAdminTarifs() {
    const res = await request(apiService.get(`${PREFIX}/admin/tarifs`));
    return {
      tarifs: res.data.tarifs ?? [],
      price_push_enabled: Boolean(res.data.price_push_enabled),
    };
  },

  async upsertAdminTarif(payload) {
    const res = await request(apiService.put(`${PREFIX}/admin/tarifs`, payload));
    return res.data.tarif;
  },

  async deactivateAdminTarif(id, { year, month }) {
    await request(apiService.post(`${PREFIX}/admin/tarifs/${id}/deactivate`, {
      from_year: year,
      from_month: month,
    }));
  },

  async reactivateAdminTarif(id) {
    await request(apiService.post(`${PREFIX}/admin/tarifs/${id}/reactivate`));
  },

  async deleteAdminTarif(id) {
    await request(apiService.delete(`${PREFIX}/admin/tarifs/${id}`));
  },

  async listAdminProduitsBdc() {
    const res = await request(apiService.get(`${PREFIX}/admin/produits-bdc`));
    return res.data.produits_bdc ?? [];
  },

  async upsertAdminProduitBdc(payload) {
    const res = await request(apiService.put(`${PREFIX}/admin/produits-bdc`, payload));
    return res.data.produit;
  },

  async deactivateAdminProduitBdc(id, { year, month }) {
    await request(apiService.post(`${PREFIX}/admin/produits-bdc/${id}/deactivate`, {
      from_year: year,
      from_month: month,
    }));
  },

  async reactivateAdminProduitBdc(id) {
    await request(apiService.post(`${PREFIX}/admin/produits-bdc/${id}/reactivate`));
  },

  async deleteAdminProduitBdc(id) {
    await request(apiService.delete(`${PREFIX}/admin/produits-bdc/${id}`));
  },

  async downloadGrilleTarifairePdf({ tarifIds = [], klubOfferIds = [] } = {}) {
    const base = getApiBaseUrl();
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const response = await fetch(`${base}${PREFIX}/admin/grille-tarifaire/pdf`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/pdf, application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tarif_ids: tarifIds,
        klub_offer_ids: klubOfferIds,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Erreur export (HTTP ${response.status})`);
    }

    const rawBlob = await response.blob();
    const blob = rawBlob.type === 'application/pdf'
      ? rawBlob
      : new Blob([rawBlob], { type: 'application/pdf' });
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match?.[1] || 'Koikispass-grille-tarifaire.pdf';
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  },
};

export default pdcService;
